import { Component, inject, signal, computed, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Services
import { AuthService } from '../../auth/services/auth.service';
import { User } from '../../../core/models/user';

// Tab Components
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { GenerateComponent } from './components/generate/generate.component';
import { ScheduledComponent } from './components/scheduled/scheduled.component';
import { HistoryComponent } from './components/history/history.component';
import { SettingsComponent } from './components/settings/settings.component';
import { SampleReportsList } from './components/scheduled/reporting-scheduled-mock-data/scheduled-reports-mock-data';
import { PageTabsComponent, PageTab } from '../../../core/components/page-tabs/page-tabs.component';
import { filter } from 'rxjs';

/**
 * Reporting V2 Page - MSP Multi-Customer Reporting
 * For Type 2 (MSP) and Type 3 (MMSP) tenants only
 */
@Component({
  selector: 'app-reporting-v2',
  standalone: true,
  imports: [
    CommonModule,
    ButtonsModule,
    DialogModule,
    DashboardComponent,
    GenerateComponent,
    ScheduledComponent,
    HistoryComponent,
    SettingsComponent,
    PageTabsComponent,
  ],
  templateUrl: './reporting-v2.page.html',
  styleUrls: ['./reporting-v2.page.scss'],
})
export class ReportingV2Page {
  @ViewChild(GenerateComponent) generateTab!: GenerateComponent;
  private route = inject(ActivatedRoute);
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private userService = inject(User);

  // Active tab management
  public activeTab = signal<string>('generate');
  public organizationId: number | null = null;
  public userId: number | null = null;

  constructor() {
    this.route.queryParams
      .pipe(
          filter(params => params['organizationId']),
          takeUntilDestroyed()
        )
      .subscribe((params) => {
      this.organizationId = params['organizationId'];
    });
  }

  ngOnInit() {
    if (this.authService.user) {
      this.userId = this.authService.user.id;
    }
  }

  // User's primary organization type
  public userPrimaryOrgType = computed(() => {
    return this.authService.user.primary_organization?.account_type ?? null;
  });

  // User's primary organization id
  public userPrimaryOrgId = computed(() => {
    return this.authService.user.primary_organization?.id ?? null;
  });

  // Sample reports dialog visibility
  public showSampleReportsDialog = signal<boolean>(false);

  // Tab definitions
  public tabs: PageTab[] = [
    // { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'generate', label: 'Generate', icon: '📄' },
    { id: 'scheduled', label: 'Scheduled', icon: '📅' },
    { id: 'history', label: 'History', icon: '📋' },
    // { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  // Sample report types with descriptions
  public sampleReportTypes = SampleReportsList;

  /**
   * Switch to a different tab
   */
  public selectTab(tabId: string): void {
    this.activeTab.set(tabId);
    console.log(`Switched to ${tabId} tab`);
  }

  /**
   * Check if tab is active
   */
  public isTabActive(tabId: string): boolean {
    return this.activeTab() === tabId;
  }

  /**
   * Quick action to navigate to Generate tab
   */
  public navigateToGenerate(): void {
    this.selectTab('generate');
  }

  /**
   * Open sample reports dialog
   */
  public openSampleReportsDialog(): void {
    this.showSampleReportsDialog.set(true);
  }

  /**
   * Close sample reports dialog
   */
  public closeSampleReportsDialog(): void {
    this.showSampleReportsDialog.set(false);

  }

  /**
   * Go to report schedule creation
   */
  public goToSaveSchedule(): void {
    this.navigateToGenerate();
    setTimeout(() => {
      this.generateTab.generateForm.get('deliveryMethod')?.setValue('schedule');
    });
  }

  /**
   * Preview a sample report in a new window
   */
  public previewSampleReport(sampleFile: string, reportName: string): void {
    this.http.get(sampleFile, { responseType: 'text' }).subscribe({
      next: (htmlContent) => {
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const newWindow = window.open(url, '_blank');

        // Clean up blob URL after window loads
        if (newWindow) {
          newWindow.addEventListener('load', () => {
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          });
        }
      },
      error: (error) => {
        console.error('Error loading sample report:', error);
        alert('Unable to load sample report. Please try downloading instead.');
      },
    });
  }

  /**
   * Download a sample report as HTML file
   */
  public downloadSampleReport(sampleFile: string, reportName: string): void {
    this.http.get(sampleFile, { responseType: 'text' }).subscribe({
      next: (htmlContent) => {
        const blob = new Blob([htmlContent], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${reportName.toLowerCase().replace(/\s+/g, '-')}-sample.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error downloading sample report:', error);
        alert('Unable to download sample report. Please try again.');
      },
    });
  }
}
